/* ============================================================
   TASK-324 (#514, EPIC-029) — Brand Kit media-storage meter.
   Pure, framework-free so it can be unit-tested without mounting
   BrandKitScreen (no @testing-library / jsdom in this repo — see
   brandKitMediaLock.js, imageAllowance.js, textAllowance.js for the same
   pure-decision convention).

   Source: GET /api/billing/entitlements' usage.media_storage_bytes =
   { used, limit, remaining } (api-gateway-worker's handleGetEntitlements,
   readMediaStorageUsage). That field is OMITTED (not present) whenever the
   gateway could not read the real cached total — never leave it at a
   fabricated 0, so "field absent" is the one true "unknown" signal here.
   Unlimited plans (limit === null) also render nothing: there is no cap to
   show progress against, and it would be pure noise vs. Settings' quiet
   unlimited line.
   ============================================================ */

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

/**
 * Human-readable byte size, e.g. formatBytes(1288490188) === "1.2 GB",
 * formatBytes(209715200) === "200 MB". One decimal place from MB up; whole
 * numbers for B/KB (fractional bytes/KB are not meaningful here).
 */
export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  let value = n;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < UNITS.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  let rounded = unitIndex === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  // Rounding can push the displayed value up to the next unit's threshold —
  // e.g. 1073741823 B is just under 1 GB, but at the MB tier it rounds to
  // "1024 MB" rather than rolling over to "1 GB". Re-check after rounding and
  // bump one more unit whenever that happens.
  if (rounded >= 1024 && unitIndex < UNITS.length - 1) {
    unitIndex += 1;
    rounded = rounded / 1024;
    rounded = unitIndex === 0 ? Math.round(rounded) : Math.round(rounded * 10) / 10;
  }
  return `${rounded} ${UNITS[unitIndex]}`;
}

/**
 * Derive the meter's render state from one usage.media_storage_bytes entry.
 * Returns { visible: false } whenever there is nothing safe to show
 * (field absent, malformed, or an unlimited plan) — BrandKitScreen renders
 * nothing at all in that case, matching the "hidden while unknown" rule.
 *
 * `usage.enforced` (gateway MEDIA_STORAGE_ENFORCED, display-only — mirrors
 * the visual-worker's STORAGE_ENFORCEMENT_ENABLED for the same env) gates
 * TONE, not visibility: the bar always shows the real used/limit fraction,
 * but while enforcement isn't actually live for this env, painting
 * warning/danger (or "full") would be a lie — no upload is being blocked at
 * that line yet. Tones are computed from the RAW used/limit values (not the
 * rounded `pct`) so a plan at exactly the 80%/100% byte boundary never
 * flips tone due to display-only rounding; `pct` is used only for the bar's
 * width.
 */
export function storageMeterState(usage) {
  const used = usage?.used;
  const limit = usage?.limit;
  if (typeof used !== 'number' || !Number.isFinite(used) || used < 0) return { visible: false };
  if (typeof limit !== 'number' || !Number.isFinite(limit) || limit <= 0) return { visible: false };
  const enforced = usage?.enforced === true;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const tone = !enforced ? 'default' : used >= limit ? 'danger' : used / limit >= 0.8 ? 'warning' : 'default';
  return {
    visible: true,
    pct,
    tone,
    enforced,
    usedLabel: formatBytes(used),
    limitLabel: formatBytes(limit),
  };
}
